import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { IComponentNode } from "../types/pageBuilder.types";

interface PageBuilderContextType {
 selectedComponent: IComponentNode | null;
 setSelectedComponent: (component: IComponentNode | null) => void;
 selectedDropTarget: string | null;
 setSelectedDropTarget: (targetId: string | null) => void;
 layout: IComponentNode | null;
 setLayout: (layout: IComponentNode | null) => void;
 updateComponent: (componentId: string, updates: Partial<IComponentNode>) => void;
 deleteComponent: (componentId: string) => void;
 addComponent: (parentId: string | null, component: IComponentNode) => void;
 duplicateComponent: (componentId: string) => void;
 findComponent: (componentId: string, tree?: IComponentNode) => IComponentNode | null;
 findParent: (componentId: string, tree?: IComponentNode, parent?: IComponentNode) => IComponentNode | null;
}

const PageBuilderContext = createContext<PageBuilderContextType | undefined>(undefined);

export const usePageBuilder = (): PageBuilderContextType => {
 const context = useContext(PageBuilderContext);
 if (!context) {
  throw new Error("usePageBuilder must be used within PageBuilderProvider");
 }
 return context;
};

interface PageBuilderProviderProps {
 children: ReactNode;
 initialLayout?: IComponentNode | null;
}

export const PageBuilderProvider: React.FC<PageBuilderProviderProps> = ({
 children,
 initialLayout = null,
}) => {
 const [selectedComponent, setSelectedComponent] = useState<IComponentNode | null>(null);
 const [selectedDropTarget, setSelectedDropTarget] = useState<string | null>(null);
 
 // Ensure root layout is always a container if not provided
 const defaultLayout: IComponentNode = {
  id: "root-container",
  type: "container",
  props: {},
  styles: { default: { padding: "1rem" } },
  children: [],
 };
 
 const [layout, setLayoutState] = useState<IComponentNode | null>(initialLayout || defaultLayout);
 
 // Wrapper to use functional update to avoid stale closure
 const setLayout = React.useCallback((newLayout: IComponentNode | null) => {
  setLayoutState((_prevLayout) => {
   return newLayout;
  });
 }, []);

 const findComponent = useCallback(
  (componentId: string, tree?: IComponentNode): IComponentNode | null => {
   const searchTree = tree || layout;
   if (!searchTree) return null;

   if (searchTree.id === componentId) {
    return searchTree;
   }

   if (searchTree.children) {
    for (const child of searchTree.children) {
     const found = findComponent(componentId, child);
     if (found) return found;
    }
   }

   return null;
  },
  [layout]
 );

 const findParent = useCallback(
  (
   componentId: string,
   tree?: IComponentNode,
   parent?: IComponentNode
  ): IComponentNode | null => {
   const searchTree = tree || layout;
   if (!searchTree) return null;

   if (searchTree.id === componentId) {
    return parent || null;
   }

   if (searchTree.children) {
    for (const child of searchTree.children) {
     const found = findParent(componentId, child, searchTree);
     if (found !== null) return found;
    }
   }

   return null;
  },
  [layout]
 );

 const updateComponent = useCallback(
  (componentId: string, updates: Partial<IComponentNode>) => {
   if (!layout) return;

   const updateInTree = (node: IComponentNode): IComponentNode => {
    if (node.id === componentId) {
     // Deep merge props and styles to preserve existing values
     const mergedProps = updates.props 
      ? { ...node.props, ...updates.props }
      : node.props;
     
     // Deep merge styles, including nested breakpoint objects
     // When a breakpoint object is provided, it replaces the entire breakpoint (not merged)
     // This allows properties to be removed by omitting them from the new object
     let mergedStyles = node.styles || {};
     if (updates.styles) {
      mergedStyles = { ...mergedStyles };
      for (const [breakpoint, breakpointStyles] of Object.entries(updates.styles)) {
       if (breakpointStyles !== null && breakpointStyles !== undefined) {
        // Replace the entire breakpoint object to allow property removal
        mergedStyles[breakpoint as keyof typeof mergedStyles] = {
         ...breakpointStyles,
        };
       }
      }
     }
     
     return { 
      ...node, 
      props: mergedProps,
      styles: mergedStyles,
      // Spread other updates (like children, animations, etc.) but don't override props/styles
      ...Object.fromEntries(
       Object.entries(updates).filter(([key]) => key !== "props" && key !== "styles")
      ),
     };
    }

    if (node.children) {
     return {
      ...node,
      children: node.children.map(updateInTree),
     };
    }

    return node;
   };

   const updatedLayout = updateInTree(layout);
   setLayout(updatedLayout);

   // Update selected component if it's the one being updated
   if (selectedComponent?.id === componentId) {
    const mergedProps = updates.props 
     ? { ...selectedComponent.props, ...updates.props }
     : selectedComponent.props;
    
    // Deep merge styles
    // When a breakpoint object is provided, it replaces the entire breakpoint (not merged)
    // This allows properties to be removed by omitting them from the new object
    let mergedStyles = selectedComponent.styles || {};
    if (updates.styles) {
     mergedStyles = { ...mergedStyles };
     for (const [breakpoint, breakpointStyles] of Object.entries(updates.styles)) {
      if (breakpointStyles !== null && breakpointStyles !== undefined) {
       // Replace the entire breakpoint object to allow property removal
       mergedStyles[breakpoint as keyof typeof mergedStyles] = {
        ...breakpointStyles,
       };
      }
     }
    }
    
    setSelectedComponent({ 
     ...selectedComponent, 
     props: mergedProps,
     styles: mergedStyles,
     // Spread other updates
     ...Object.fromEntries(
      Object.entries(updates).filter(([key]) => key !== "props" && key !== "styles")
     ),
    });
   }
  },
  [layout, selectedComponent, setLayout]
 );

 const deleteComponent = useCallback(
  (componentId: string) => {
   if (!layout) return;

   // Don't allow deleting root component
   if (layout.id === componentId) {
    return;
   }

   const deleteInTree = (node: IComponentNode): IComponentNode | null => {
    if (node.children) {
     const filteredChildren = node.children
      .filter((child) => child.id !== componentId)
      .map(deleteInTree)
      .filter((child): child is IComponentNode => child !== null);

     return {
      ...node,
      children: filteredChildren,
     };
    }

    return node;
   };

   const updatedLayout = deleteInTree(layout);
   setLayout(updatedLayout);
   setSelectedComponent(null);
  },
  [layout, setLayout]
 );

 const addComponent = useCallback(
  (parentId: string | null, component: IComponentNode) => {
   if (!layout) return;

   if (parentId === null) {
    // Add as root (replace current layout)
    setLayout(component);
    return;
   }

   const addInTree = (node: IComponentNode): IComponentNode => {
    if (node.id === parentId) {
     return {
      ...node,
      children: [...(node.children || []), component],
     };
    }

    if (node.children) {
     return {
      ...node,
      children: node.children.map(addInTree),
     };
    }

    return node;
   };

   const updatedLayout = addInTree(layout);
   setLayout(updatedLayout);
  },
  [layout, setLayout]
 );

 const duplicateComponent = useCallback(
  (componentId: string) => {
   const component = findComponent(componentId);
   if (!component || !layout) return;

   const parent = findParent(componentId);
   if (!parent) return;

   const duplicate = {
    ...component,
    id: `${component.id}-copy-${Date.now()}`,
    children: component.children
     ? component.children.map((child) => ({
       ...child,
       id: `${child.id}-copy-${Date.now()}`,
      }))
     : undefined,
   };

   const addInTree = (node: IComponentNode): IComponentNode => {
    if (node.id === parent.id) {
     const index = node.children?.findIndex((c) => c.id === componentId) ?? -1;
     const newChildren = [...(node.children || [])];
     newChildren.splice(index + 1, 0, duplicate);
     return {
      ...node,
      children: newChildren,
     };
    }

    if (node.children) {
     return {
      ...node,
      children: node.children.map(addInTree),
     };
    }

    return node;
   };

   const updatedLayout = addInTree(layout);
   setLayout(updatedLayout);
  },
  [layout, findComponent, findParent, setLayout]
 );

 return (
  <PageBuilderContext.Provider
   value={{
    selectedComponent,
    setSelectedComponent,
    selectedDropTarget,
    setSelectedDropTarget,
    layout,
    setLayout,
    updateComponent,
    deleteComponent,
    addComponent,
    duplicateComponent,
    findComponent,
    findParent,
   }}
  >
   {children}
  </PageBuilderContext.Provider>
 );
};

